export default class extends HTMLElement {
  connectedCallback () {
    this.disclosure = this.querySelector('[data-delivery-disclosure]')
    this.details = this.querySelector('[data-delivery-details]')
    this.disclosure?.addEventListener('click', this.onDisclosure)
    this.moreTrigger = this.querySelector('[data-more-actions-trigger]')
    this.moreMenu = this.querySelector('[data-more-actions-menu]')
    this.moreTrigger?.addEventListener('click', this.onMoreActions)

    this.manualTrigger = this.querySelector('[data-open-manual-dialog]')
    this.manualDialog = this.querySelector('[data-manual-dialog]')
    this.manualTrigger?.addEventListener('click', this.openManualDialog)

    this.resendTrigger = this.querySelector('[data-open-resend-dialog]')
    this.resendDialog = this.querySelector('[data-resend-dialog]')
    this.resendTrigger?.addEventListener('click', this.openResendDialog)

    this.dialogs.forEach((dialog) => {
      dialog.addEventListener('keydown', this.onDialogKeydown)
      dialog.addEventListener('cancel', this.onDialogCancel)
      dialog.querySelector('[data-dialog-cancel]').addEventListener('click', this.closeDialog)
      dialog.querySelector('[data-dialog-confirm]').addEventListener('click', this.confirmDialog)
    })
  }

  disconnectedCallback () {
    this.disclosure?.removeEventListener('click', this.onDisclosure)
    this.moreTrigger?.removeEventListener('click', this.onMoreActions)
    this.manualTrigger?.removeEventListener('click', this.openManualDialog)
    this.resendTrigger?.removeEventListener('click', this.openResendDialog)
    this.dialogs.forEach((dialog) => {
      dialog.removeEventListener('keydown', this.onDialogKeydown)
      dialog.removeEventListener('cancel', this.onDialogCancel)
      dialog.querySelector('[data-dialog-cancel]').removeEventListener('click', this.closeDialog)
      dialog.querySelector('[data-dialog-confirm]').removeEventListener('click', this.confirmDialog)
    })
    this.abortController?.abort()
  }

  get dialogs () {
    return [...this.querySelectorAll('dialog[data-manual-dialog], dialog[data-resend-dialog]')]
  }

  onDisclosure = () => {
    const expanded = this.disclosure.getAttribute('aria-expanded') === 'true'

    this.disclosure.setAttribute('aria-expanded', String(!expanded))
    this.details.classList.toggle('hidden', expanded)
  }

  onMoreActions = () => {
    const expanded = this.moreTrigger.getAttribute('aria-expanded') === 'true'

    this.moreTrigger.setAttribute('aria-expanded', String(!expanded))
    this.moreMenu.classList.toggle('hidden', expanded)
  }

  openManualDialog = () => this.openDialog(this.manualDialog, this.manualTrigger)

  openResendDialog = () => this.openDialog(this.resendDialog, this.resendTrigger)

  openDialog (dialog, trigger) {
    if (!dialog) return

    this.activeDialog = dialog
    this.dialogTrigger = trigger
    dialog.showModal()
    dialog.querySelector('[data-dialog-cancel]').focus()
  }

  onDialogCancel = (event) => {
    event.preventDefault()
    this.closeActiveDialog()
  }

  closeDialog = () => this.closeActiveDialog()

  closeActiveDialog () {
    if (!this.activeDialog?.open) return

    this.activeDialog.close()
    const trigger = this.dialogTrigger
    this.activeDialog = null
    this.dialogTrigger = null
    trigger?.focus()
  }

  onDialogKeydown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      this.closeActiveDialog()
      return
    }

    if (event.key !== 'Tab') return

    const controls = [...event.currentTarget.querySelectorAll('button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])')]
    if (!controls.length) return

    const first = controls[0]
    const last = controls[controls.length - 1]

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  confirmDialog = async (event) => {
    const dialog = event.currentTarget.closest('dialog')
    const confirm = event.currentTarget

    confirm.disabled = true
    this.hideMessages()

    try {
      const data = await this.postConfirmation(dialog.dataset.endpoint)

      if (dialog.hasAttribute('data-manual-dialog')) {
        await navigator.clipboard.writeText(data.url)
        this.showSuccess(this.dataset.manualSuccess)
      } else {
        this.querySelector('[data-delivery-badge] span:last-child').textContent = 'Queued'
        this.resendTrigger.disabled = true
        this.showSuccess(this.dataset.resendSuccess)
      }

      this.closeActiveDialog()
    } catch (error) {
      const data = error.data

      if (dialog.hasAttribute('data-resend-dialog') && data?.reason === 'try_again') {
        const duration = data.retry_after || '1 hour'
        this.resendTrigger.textContent = `${this.dataset.resendRetryPrefix} ${duration}`
        this.resendTrigger.disabled = true
      }

      this.showError()
      this.closeActiveDialog()
    } finally {
      confirm.disabled = false
    }
  }

  async postConfirmation (url) {
    this.abortController?.abort()
    this.abortController = new AbortController()
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      signal: this.abortController.signal,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
      },
      body: JSON.stringify({ confirmed: true })
    })
    const data = await response.json().catch(() => ({}))

    if (!response.ok) {
      const error = new Error('Confirmation failed')
      error.data = data
      throw error
    }

    return data
  }

  hideMessages () {
    this.querySelector('[data-delivery-success]').classList.add('hidden')
    this.querySelector('[data-delivery-success]').classList.remove('flex')
    this.querySelector('[data-delivery-error]').classList.add('hidden')
    this.querySelector('[data-delivery-error]').classList.remove('flex')
  }

  showSuccess (copy) {
    const message = this.querySelector('[data-delivery-success]')
    message.querySelector('[data-delivery-success-copy]').textContent = copy
    message.classList.remove('hidden')
    message.classList.add('flex')
  }

  showError () {
    const message = this.querySelector('[data-delivery-error]')
    message.classList.remove('hidden')
    message.classList.add('flex')
  }
}
